import { PageTypes, type FormStopStyle } from './constants';
import type { QuestionDataInput } from './schemas/form/question-data';

export type PageTypeTypes = (typeof PageTypes)[keyof typeof PageTypes];

// --- Shared UI / Style Types --- //
export type StyleBoxSides = {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
};

export type StyleCornerSides = {
  topLeft?: string;
  topRight?: string;
  bottomRight?: string;
  bottomLeft?: string;
};

export type GradientStop = {
  color: string;
  at: number;
};

export type StyleBackgroundLayer =
  | { kind: 'color'; color: string }
  | { kind: 'image'; url: string; size?: string; position?: string; repeat?: string }
  | { kind: 'linear'; angle: number; stops: GradientStop[] }
  | { kind: 'radial'; shape: 'circle' | 'ellipse'; position?: string; stops: GradientStop[] };

export type StyleProperties = {
  margin?: StyleBoxSides;
  padding?: StyleBoxSides;
  borderRadius?: StyleCornerSides;
  backgroundColor?: string;
  backgroundLayers?: StyleBackgroundLayer[];
};

export type StyleValue = {
  mobile?: StyleProperties;
  desktop?: StyleProperties;
};

export type StyleBreakpoint = keyof StyleValue;

export type TextStyleFontFamily = 'serif' | 'sans';

export type TextStyleTextAlign = 'left' | 'center' | 'right';

export type TextStyleProperties = {
  color?: string;
  textAlign?: TextStyleTextAlign;
  fontFamily?: TextStyleFontFamily;
  fontWeight?: string;
  fontSize?: string;
  lineHeight?: string;
  letterSpacing?: string;
  margin?: StyleBoxSides;
  padding?: StyleBoxSides;
};

export type TextStyleValue = {
  mobile?: TextStyleProperties;
  desktop?: TextStyleProperties;
};

export type BackgroundValue = {
  color?: string;
  layers?: StyleBackgroundLayer[];
};

export type GenericOption = {
  id: string | number;
  value?: string | number | boolean;
  label?: string;
  option?: string;
  i18n?: Record<string, { label: string }>;
  score?: number;
};

// --- Form State Types --- //
export type AnswerValue = {
  value: string | number | boolean | (string | number | boolean)[] | null;
  score: number[];
};

export type Answers = Record<string, AnswerValue>;

export type StepStatus = {
  step: number;
  content: {
    type: string;
    action: { name: string | null; metadata?: Record<string, string | number | boolean | null> };
    is_fulfilled: boolean;
    answers: Answers;
  }[];
  action: { name: string | null; metadata?: Record<string, string | number | boolean | null> };
  is_fulfilled: boolean;
};

export type FormState = {
  currentStepIndex: number;
  answers: Answers;
  isFinished: boolean;
  isShowingNested: boolean;
  isQualified: boolean;
  isDuplicate: boolean;
};

/**
 * The active `stop`, carrying everything the Renderer must display and where to display it. A stop
 * is never terminal — the visitor re-answers past it — so this is cleared as soon as the answer
 * changes or the message is dismissed.
 */
export type FormStopState = {
  /** Empty when the criterion was authored without a message; the Renderer falls back to its own. */
  message: string;
  style: FormStopStyle;
  /** Index, within the current level, of the content whose criterion stopped. */
  contentIndex: number;
  /**
   * `question_id`s (as strings) of the rules the stopping criterion matched on — the answers that
   * were dropped. An inline message anchors right after the last of them.
   */
  questionIds: string[];
};

/** A single step's contents and the answers given in it. */
export type FormStepPayload = { step: number; content: { type: string; answers: Answers }[] };

/** Everything the Renderer knows about the visitor once the last step is answered. */
export type FormSubmitPayload = {
  answers: Answers;
  is_qualified: boolean;
  current_step: number;
  total_steps: number;
  is_finished: boolean;
};

/** Lead outcome resolved after submission; drives which completion message is shown. */
export type FormLeadStatus = {
  is_qualified: boolean;
  is_duplicate: boolean;
};

export type FormConsentOption = {
  id: number;
  title: string;
  is_default?: boolean;
  i18n: {
    en: { consent_statement: string };
    es: { consent_statement: string };
  };
};

export type FormMessageOption = {
  id: number;
  name: string;
  is_default?: boolean;
};

/**
 * A single completion message resolved for one locale, ready to render: HTML strings for the
 * callouts/title/message and, for the qualified timeline, each step's icon already replaced by
 * its SVG markup.
 */
export type FormCompletionStep = { icon: string; title: string; description: string };
export type FormCompletionQualified = {
  alert: string;
  title: string;
  help: string;
  steps: FormCompletionStep[];
};
export type FormCompletionResult = { alert: string; title: string; help: string; message: string };
export type FormCompletionContent = {
  qualified: FormCompletionQualified;
  not_qualified: FormCompletionResult;
  duplicated: FormCompletionResult;
};

export type FormImageUploadConfig = {
  apiPath: string;
  defaultFolder?: string;
  onUpload: (
    file: File,
    destDir: string,
    dimensions?: { maxWidth: number; maxHeight: number }
  ) => Promise<string>;
};

export type Form = {
  id: number;
  name: string;
  criteria: unknown[];
  metadata: unknown;
  claim_section_id: number;
  form_message_id: number;
  form_steps: unknown;
};

/** A single address suggestion returned by `searchAddress`. */
export type AddressSuggestion = { description: string; place_id: string };

/**
 * Field validation/lookup the renderer's address, zip-code and phone fields depend on.
 * The `form` package declares the contract; the host app (apps/web) provides the
 * implementation (e.g. server actions backed by libphonenumber / Google), so the package
 * stays free of any provider. When a validator is absent, fields fall back to their own
 * lightweight checks.
 */
export type FormFieldValidators = {
  validatePhone?: (phone: string) => Promise<{ is_valid: boolean }>;
  validatePostalCode?: (
    code: string
  ) => Promise<{ is_valid: boolean; city?: string; state?: string }>;
  validateAddress?: (address: string) => Promise<{ is_valid: boolean; formatted?: string }>;
  searchAddress?: (query: string) => Promise<AddressSuggestion[]>;
};

/**
 * Host overrides for the renderer's validation messages. Any key left out falls back to the
 * built-in localized default. `'required'` covers empty required fields; the rest are format errors.
 */
export type FormErrorMessages = Partial<
  Record<'required' | 'invalid_email' | 'invalid_number' | 'invalid_phone' | 'invalid_zip', string>
>;

export type FormConfig = {
  availableQuestions?: QuestionDataInput[];
  formConsentOptions?: FormConsentOption[];
  formMessageOptions?: FormMessageOption[];
  /** Resolved completion message for the active locale, rendered by the host via `renderCompletion`. */
  completionMessage?: FormCompletionContent;
  imageUpload?: FormImageUploadConfig;
  validators?: FormFieldValidators;
  errorMessages?: FormErrorMessages;
  showErrors?: boolean;
};

export type CriteriaRuleTypeValues = 'some-match' | 'every-match' | 'none-match';
export type CriteriaRule = {
  question_id: number;
  rule: CriteriaRuleTypeValues;
  expected: (string | number | boolean)[];
};
